import React from 'react';
import { Flex, Grid, Skeleton, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS } from '../i18n';
// PageShell's and the list's own sheets: the skeleton is the route's Suspense
// fallback and renders before Account's chunk. NOT Account.css — RouteSkeleton
// ships in the initial bundle, and that sheet styles classes other screens
// share (avatar-icon, process-flow-filters…), so it would restyle them.
import '../../common/components/PageShell/PageShell.css';
import './AccountsList.css';

interface AccountSkeletonProps {
  isMobile?: boolean;
}

/* The loading state wears the same shell as the loaded screen — the classes
   come from PageShell, so the two cannot drift apart. The title block is 38px
   tall because that is lineHeightHeading1. */
export const AccountFullPageSkeleton: React.FC<AccountSkeletonProps> = ({ isMobile = false }) => {
  const { t } = useTranslation(NS);
  return (
    <div className="page-shell" aria-busy="true" aria-label={t('list.loadingAria')}>
      {/* Breadcrumb: the loaded page has one above the card, so the card must not jump. */}
      <div className="app-breadcrumb">
        <Skeleton.Input active size="small" style={{ width: 200, minWidth: 200, height: 22 }} />
      </div>
      <div className="page-shell__card">
        <header className="page-shell__header">
          <Skeleton
            loading={true}
            active
            title={{ width: 200, style: { height: 38 } }}
            paragraph={false}
          />
          <Skeleton.Button
            active
            size="large"
            style={{
              width: isMobile ? '100%' : 140,
              height: 40,
              borderRadius: 6,
            }}
          />
        </header>

        {/* Search and filters section */}
        <div className="page-shell__toolbar">
          <Skeleton.Input
            active
            size="large"
            style={{
              width: isMobile ? '100%' : 280,
              height: 40,
              borderRadius: 6,
              marginBottom: isMobile ? 8 : 0,
            }}
          />
          {/* The page's own structure: label (text + icon), then the three Selects
              (Status · Currency · Payment) as direct children, gap 16. */}
          <Flex align="center" gap={16} className="accounts-filters-section">
            <Flex align="center" gap={8} className="accounts-filters-label">
              <Skeleton.Input active style={{ width: 60, minWidth: 60, height: 20 }} size="small" />
            </Flex>
            {[0, 1, 2].map((index) => (
              <Skeleton.Button
                key={index}
                active
                size="default"
                className="accounts-filter-select"
                style={{ width: 100, height: 32, borderRadius: 6 }}
              />
            ))}
          </Flex>
        </div>

        {/* Table skeleton */}
        <div className="table-box accounts-table-container">
          <Table
            columns={[
              {
                title: <Skeleton.Input active style={{ width: 100, height: 16 }} size="small" />,
                key: 'account',
                render: () => (
                  <Flex align="center" gap={12}>
                    <Skeleton.Avatar active size="large" shape="square" />
                    <Flex vertical>
                      <Skeleton.Input
                        active
                        style={{ width: 150, height: 16, marginBottom: 4 }}
                        size="small"
                      />
                      <Flex gap={16}>
                        <Skeleton.Input active style={{ width: 120, height: 14 }} size="small" />
                        <Skeleton.Input active style={{ width: 60, height: 14 }} size="small" />
                      </Flex>
                    </Flex>
                  </Flex>
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 120, height: 16 }} size="small" />,
                key: 'monthlyBaseFee',
                render: () => (
                  <Skeleton.Input active style={{ width: 80, height: 16 }} size="small" />
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 100, height: 16 }} size="small" />,
                key: 'averageExtra',
                render: () => (
                  <Skeleton.Input active style={{ width: 80, height: 16 }} size="small" />
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 80, height: 16 }} size="small" />,
                key: 'method',
                render: () => (
                  <Skeleton.Input active style={{ width: 60, height: 16 }} size="small" />
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 80, height: 16 }} size="small" />,
                key: 'payment',
                render: () => (
                  <Skeleton.Button active style={{ width: 70, height: 22 }} size="small" />
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 70, height: 16 }} size="small" />,
                key: 'status',
                render: () => (
                  <Skeleton.Button active style={{ width: 60, height: 22 }} size="small" />
                ),
              },
              {
                title: <Skeleton.Input active style={{ width: 100, height: 16 }} size="small" />,
                key: 'action',
                render: () => (
                  <Skeleton.Button active style={{ width: 120, height: 32 }} size="default" />
                ),
              },
              {
                title: '',
                key: 'menu',
                render: () => (
                  <Skeleton.Button
                    active
                    shape="circle"
                    style={{ width: 24, height: 24 }}
                    size="small"
                  />
                ),
              },
            ]}
            dataSource={Array.from({ length: 6 }, (_, index) => ({ key: `skeleton-${index}` }))}
            pagination={false}
            className="accounts-table"
            scroll={{ x: 'max-content' }}
          />
          <div className="table-footer">
            <Skeleton.Input active style={{ width: 150, height: 16 }} size="small" />
            <Skeleton.Button
              active
              style={{ width: 200, height: 32, borderRadius: 6 }}
              size="default"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The Accounts Manager route's Suspense fallback (SL-1835): the same skeleton
 * the screen shows while its data loads, so a cold load paints one shape.
 */
export const AccountsPageSkeleton: React.FC = () => {
  const screens = Grid.useBreakpoint();
  return <AccountFullPageSkeleton isMobile={!screens.md} />;
};

/**
 * Loading placeholder for the accounts table's rows.
 *
 * The Table used to be handed `loading` with an emptied dataSource, so AntD drew
 * its centred Spin over a blank body: the rows vanished and the box collapsed to
 * the spinner's height while the header and footer stayed. This keeps the block
 * the same size and mirrors what each of the eight columns renders — an avatar
 * with two lines of text for Account, plain values for the three amounts, a pill
 * for Payment and Status, a button for Action, and the row menu.
 *
 * Follows InvoicesTableSkeleton: the whole block is replaced rather than a
 * `<tbody>` injected through `components`, which would depend on AntD's internal
 * class names.
 */
export const AccountTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Flex vertical>
    {/* Header — eight column labels */}
    <Flex
      align="center"
      gap={16}
      style={{
        padding: 'var(--silia-space-sm) var(--silia-space-md)',
        borderBottom: '1px solid var(--silia-border-subtle)',
      }}
    >
      {[180, 96, 88, 72, 80, 68, 96, 32].map((w, i) => (
        <Skeleton.Input key={i} active size="small" style={{ width: w, height: 14 }} />
      ))}
    </Flex>

    {/* Rows */}
    {Array.from({ length: rows }).map((_, r) => (
      <Flex
        key={r}
        align="center"
        gap={16}
        style={{
          padding: 'var(--silia-space-md)',
          borderBottom: '1px solid var(--silia-border-subtle)',
        }}
      >
        {/* Account — avatar + name + two secondary lines */}
        <Flex align="center" gap={12} style={{ width: 180, flexShrink: 0 }}>
          <Skeleton.Avatar size="large" shape="square" active />
          <Flex vertical gap={4}>
            <Skeleton.Input active size="small" style={{ width: 112, height: 16 }} />
            <Skeleton.Input active size="small" style={{ width: 88, height: 12 }} />
          </Flex>
        </Flex>
        {/* Monthly Base Fee · Average Extra · Method */}
        <Skeleton.Input active size="small" style={{ width: 96, height: 16 }} />
        <Skeleton.Input active size="small" style={{ width: 88, height: 16 }} />
        <Skeleton.Input active size="small" style={{ width: 72, height: 16 }} />
        {/* Payment · Status — Tag pills */}
        <Skeleton.Button
          active
          size="small"
          style={{ width: 80, height: 22, borderRadius: 'var(--silia-radius-pill)' }}
        />
        <Skeleton.Button
          active
          size="small"
          style={{ width: 68, height: 22, borderRadius: 'var(--silia-radius-pill)' }}
        />
        {/* Action — Button */}
        <Skeleton.Button active size="small" style={{ width: 96, height: 32 }} />
        {/* Row menu */}
        <Skeleton.Avatar size="small" shape="circle" active />
      </Flex>
    ))}
  </Flex>
);
